import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ToastContainer,toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import BaseUrl from '../../../Url/BaseUrl';

const AddTimetablePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [routePaths, setRoutePaths] = useState([]);
  const [busStands, setBusStands] = useState([]);
  const [buses, setBuses] = useState([]);
  const [entries, setEntries] = useState([
    {
      id: 1,
      routePathNo: '',
      busStandCode: '',
      busNo: '',
      arrivalTime: '',
      departureTime: '',
      status: true
    }
  ]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchRoutePaths();
  }, []);

  // Fetch all route paths
  const fetchRoutePaths = async () => {
    try {
      const response = await axios.get(`${BaseUrl.baseurl}RoutePaths`);
      setRoutePaths(response.data);
    } catch (error) {
      console.error('Failed to fetch route paths:', error);
      alert(t('failedToFetchRoutePaths'));
    }
  };

  // Fetch bus stands for selected route path
  const fetchBusStandsByRoutePath = async (routePathId) => {
    try {
      const response = await axios.get(
        `${BaseUrl.baseurl}BusStands/GetBusStandsByRoutePathId?rpi=${routePathId}`
      );
      setBusStands(response.data);
    } catch (error) {
      console.error('Failed to fetch bus stands:', error);
      setBusStands([]);
    }
  };

  // Fetch buses for selected route path
  const fetchBusesByRoutePath = async (routePathId) => {
    try {
      const response = await axios.get(
        `${BaseUrl.baseurl}Buses/GetBusesByRoutePathId?routpathid=${routePathId}`
      );
      setBuses(response.data);
    } catch (error) {
      console.error('Failed to fetch buses:', error);
      setBuses([]);
    }
  };

  const handleRoutePathChange = (index, value) => {
    const updatedEntries = [...entries];
    const entry = updatedEntries[index];
    
    // Reset dependent fields for this entry
    entry.routePathNo = value;
    entry.busStandCode = '';
    entry.busNo = '';
    entry.arrivalTime = '';
    entry.departureTime = '';
    
    // Clear errors for this entry
    const newErrors = { ...errors };
    delete newErrors[`${index}-routePathNo`];
    delete newErrors[`${index}-busStandCode`];
    delete newErrors[`${index}-busNo`];
    delete newErrors[`${index}-arrivalTime`];
    delete newErrors[`${index}-departureTime`];
    setErrors(newErrors);
    
    setEntries(updatedEntries);

    if (value) {
      // Fetch both bus stands and buses for the selected route path
      fetchBusStandsByRoutePath(value);
      fetchBusesByRoutePath(value);
    } else {
      setBusStands([]);
      setBuses([]);
    }
  };

  const handleInputChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedEntries = [...entries];
    
    updatedEntries[index][name] = type === 'checkbox' ? checked : value;
    
    // Clear error for this field
    const newErrors = { ...errors };
    delete newErrors[`${index}-${name}`];
    setErrors(newErrors);
    
    setEntries(updatedEntries);
  };

  const addNewEntry = () => {
    const newId = entries.length > 0 ? Math.max(...entries.map(e => e.id)) + 1 : 1;
    setEntries([
      ...entries,
      {
        id: newId,
        routePathNo: '',
        busStandCode: '',
        busNo: '',
        arrivalTime: '',
        departureTime: '',
        status: true
      }
    ]);
  };

  const removeEntry = (index) => {
    if (entries.length <= 1) return;
    
    const updatedEntries = [...entries];
    updatedEntries.splice(index, 1);
    
    // Remove errors for this entry
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach(key => {
      if (key.startsWith(`${index}-`)) {
        delete newErrors[key];
      }
    });
    
    setEntries(updatedEntries);
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {};
    
    entries.forEach((entry, index) => {
      if (!entry.routePathNo) newErrors[`${index}-routePathNo`] = t('routePathRequired');
      if (!entry.busStandCode) newErrors[`${index}-busStandCode`] = t('busStandRequired');
      if (!entry.busNo) newErrors[`${index}-busNo`] = t('busNumberRequired');
      if (!entry.arrivalTime) newErrors[`${index}-arrivalTime`] = t('arrivalTimeRequired');
      if (!entry.departureTime) newErrors[`${index}-departureTime`] = t('departureTimeRequired');
      
      // Validate that arrival time is before departure time
      if (entry.arrivalTime && entry.departureTime) {
        const arrival = new Date(`1970-01-01T${entry.arrivalTime}`);
        const departure = new Date(`1970-01-01T${entry.departureTime}`);
        if (arrival >= departure) {
          newErrors[`${index}-departureTime`] = t('departureAfterArrival');
        }
      }
    });
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    
    try {
      const payload = entries.map(entry => {
        // Format time to match System.TimeOnly format - try different formats
        const formatTime = (timeString) => {
          if (!timeString) return '';
          
          // Try format: HH:mm:ss.fff (without Z)
          if (timeString.length === 5) { // HH:mm
            return `${timeString}:00.000`;
          }
          if (timeString.length === 8) { // HH:mm:ss
            return `${timeString}.000`;
          }
          return timeString;
        };
        
        // Generate a unique number for baab (based on timestamp and index)
        const baabValue = Date.now() + Math.floor(Math.random() * 1000) + entries.indexOf(entry);
        
        return {
          id: 0,
          busStandCode: entry.busStandCode,
          busStand: null,
          busNo: parseInt(entry.busNo, 10),
          bus: null,
          arrivalTime: formatTime(entry.arrivalTime),
          departureTime: formatTime(entry.departureTime),
          status: entry.status,
          baab: baabValue // Add the required baab field
        };
      });
      
      console.log('Submitting payload:', JSON.stringify(payload, null, 2));
      toast.success("added successfully",{ style: {
    background: '#ffffff',
    color: '#1f2937',
    border: '1px solid #e5e7eb'
  }})
      // Try different time formats if first attempt fails
      let response;
      let lastError;
      
      // Try format 1: Without Z
      try {
        response = await axios.post(
          `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
      } catch (error1) {
        lastError = error1;
        console.log('First attempt failed, trying with Z suffix...');
        
        // Try format 2: With Z suffix
        const payloadWithZ = payload.map(item => ({
          ...item,
          arrivalTime: item.arrivalTime.endsWith('Z') ? item.arrivalTime : `${item.arrivalTime}Z`,
          departureTime: item.departureTime.endsWith('Z') ? item.departureTime : `${item.departureTime}Z`
        }));
        
        try {
          response = await axios.post(
            `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands`,
            payloadWithZ,
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (error2) {
          lastError = error2;
          console.log('Second attempt failed, trying HH:mm format...');
          
          // Try format 3: Just HH:mm (no seconds)
          const payloadSimpleTime = payload.map(item => ({
            ...item,
            arrivalTime: item.arrivalTime.substring(0, 5), // Just HH:mm
            departureTime: item.departureTime.substring(0, 5) // Just HH:mm
          }));
          
          try {
            response = await axios.post(
              `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands`,
              payloadSimpleTime,
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );
          } catch (error3) {
            lastError = error3;
            throw error3;
          }
        }
      }

      console.log('Response:', response.data);
      setSuccessMessage(t('timetableEntriesAdded', { count: entries.length }));
      
      // Reset form with one empty entry
      setEntries([{
        id: 1,
        routePathNo: '',
        busStandCode: '',
        busNo: '',
        arrivalTime: '',
        departureTime: '',
        status: true
      }]);
      setBusStands([]);
      setBuses([]);
      setErrors({});
      
    } catch (error) {
      console.error('Full error details:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 400) {
        // Show validation errors if available
        if (error.response.data?.errors) {
          const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          alert(`Validation failed:\n${errorMessages}\n\nPlease check:\n1. Time format should be HH:mm:ss.fff (e.g., 06:09:00.000)\n2. All required fields are filled`);
        } else if (error.response.data?.title) {
          alert(`${error.response.data.title}: ${error.response.data.detail || 'Please check your input'}`);
        } else {
          alert(t('failedToAddTimetableEntries'));
        }
      } else {
        alert(t('failedToAddTimetableEntries'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEntries([{
      id: 1,
      routePathNo: '',
      busStandCode: '',
      busNo: '',
      arrivalTime: '',
      departureTime: '',
      status: true
    }]);
    setBusStands([]);
    setBuses([]);
    setErrors({});
    setSuccessMessage('');
  };

  const duplicateEntry = (index) => {
    const entryToDuplicate = entries[index];
    const newId = entries.length > 0 ? Math.max(...entries.map(e => e.id)) + 1 : 1;
    
    setEntries([
      ...entries,
      {
        id: newId,
        routePathNo: entryToDuplicate.routePathNo,
        busStandCode: entryToDuplicate.busStandCode,
        busNo: entryToDuplicate.busNo,
        arrivalTime: entryToDuplicate.arrivalTime,
        departureTime: entryToDuplicate.departureTime,
        status: entryToDuplicate.status
      }
    ]);
  };

  return (
    <div className="timetable-container">
      <ToastContainer/>
      
      {/* Back Button */}
      <div className="back-button-container">
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
      
      <div className="card">
        <div className="card-header">
          <h2>{t('addBusTimetable')}</h2>
          <p className="subtitle">{t('addMultipleEntries')}</p>
        </div>
        <div className="card-body">
          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>{t('addingTimetableEntries')}</p>
            </div>
          )}

          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="timetable-form">
            {entries.map((entry, index) => (
              <div key={entry.id} className="entry-card">
                <div className="entry-header">
                  <h3>{t('timetableEntry')} #{index + 1}</h3>
                  {entries.length > 1 && (
                    <button 
                      type="button" 
                      className="btn-remove"
                      onClick={() => removeEntry(index)}
                      title={t('removeEntry')}
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Step 1: Select Route Path */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`routePathNo-${index}`}>{t('routePath')} *</label>
                    <select
                      id={`routePathNo-${index}`}
                      name="routePathNo"
                      value={entry.routePathNo}
                      onChange={(e) => handleRoutePathChange(index, e.target.value)}
                      className={errors[`${index}-routePathNo`] ? 'error' : ''}
                    >
                      <option value="">{t('selectRoutePath')}</option>
                      {routePaths.map(route => (
                        <option key={route.routePathNo} value={route.routePathNo}>
                          {route.routePathName} ({route.routePathNo})
                        </option>
                      ))}
                    </select>
                    {errors[`${index}-routePathNo`] && (
                      <span className="error-text">{errors[`${index}-routePathNo`]}</span>
                    )}
                  </div>
                </div>

                {/* Step 2: Select Bus Stand and Bus */}
                {entry.routePathNo && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`busStandCode-${index}`}>{t('busStand')} *</label>
                      <select
                        id={`busStandCode-${index}`}
                        name="busStandCode"
                        value={entry.busStandCode}
                        onChange={(e) => handleInputChange(index, e)}
                        className={errors[`${index}-busStandCode`] ? 'error' : ''}
                        disabled={busStands.length === 0}
                      >
                        <option value="">
                          {busStands.length === 0 ? t('loadingBusStands') : t('selectBusStand')}
                        </option>
                        {busStands.map(stand => (
                          <option key={stand.busStandCode} value={stand.busStandCode}>
                            {stand.busStandName} ({stand.busStandCode})
                            {!stand.status && ` [${t('inactive')}]`}
                          </option>
                        ))}
                      </select>
                      {errors[`${index}-busStandCode`] && (
                        <span className="error-text">{errors[`${index}-busStandCode`]}</span>
                      )}
                      {busStands.length === 0 && entry.routePathNo && (
                        <span className="info-text">{t('noBusStandsFound')}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor={`busNo-${index}`}>{t('busNumber')} *</label>
                      <select
                        id={`busNo-${index}`}
                        name="busNo"
                        value={entry.busNo}
                        onChange={(e) => handleInputChange(index, e)}
                        className={errors[`${index}-busNo`] ? 'error' : ''}
                        disabled={buses.length === 0}
                      >
                        <option value="">
                          {buses.length === 0 ? t('loadingBuses') : t('selectBus')}
                        </option>
                        {buses.map(bus => (
                          <option key={bus.busNo} value={bus.busNo}>
                            {bus.busNo} - {bus.busName} ({bus.busRegistrationNumber})
                            {!bus.status && ` [${t('inactive')}]`}
                          </option>
                        ))}
                      </select>
                      {errors[`${index}-busNo`] && (
                        <span className="error-text">{errors[`${index}-busNo`]}</span>
                      )}
                      {buses.length === 0 && entry.routePathNo && (
                        <span className="info-text">{t('noBusesFound')}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Time inputs */}
                {entry.busStandCode && entry.busNo && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`arrivalTime-${index}`}>{t('arrivalTime')} *</label>
                        <input
                          type="time"
                          id={`arrivalTime-${index}`}
                          name="arrivalTime"
                          value={entry.arrivalTime}
                          onChange={(e) => handleInputChange(index, e)}
                          className={errors[`${index}-arrivalTime`] ? 'error' : ''}
                          step="300"
                        />
                        {errors[`${index}-arrivalTime`] && (
                          <span className="error-text">{errors[`${index}-arrivalTime`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor={`departureTime-${index}`}>{t('departureTime')} *</label>
                        <input
                          type="time"
                          id={`departureTime-${index}`}
                          name="departureTime"
                          value={entry.departureTime}
                          onChange={(e) => handleInputChange(index, e)}
                          className={errors[`${index}-departureTime`] ? 'error' : ''}
                          step="300"
                        />
                        {errors[`${index}-departureTime`] && (
                          <span className="error-text">{errors[`${index}-departureTime`]}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="status"
                            checked={entry.status}
                            onChange={(e) => handleInputChange(index, e)}
                            className="checkbox-input"
                          />
                          <span className="checkbox-custom"></span>
                          <span className="checkbox-text">{t('active')}</span>
                        </label>
                      </div>
                      
                      <div className="form-group">
                        <button 
                          type="button" 
                          className="btn-duplicate"
                          onClick={() => duplicateEntry(index)}
                          disabled={!entry.busStandCode || !entry.busNo}
                        >
                          {t('duplicateEntry')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                
                {index < entries.length - 1 && <hr className="entry-divider" />}
              </div>
            ))}

            <div className="form-actions-container">
              <div className="entry-controls">
                <button 
                  type="button" 
                  className="btn-add-entry"
                  onClick={addNewEntry}
                >
                  + {t('addAnotherEntry')}
                </button>
                <span className="entry-count">
                  {t('totalEntries')}: {entries.length}
                </span>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading || entries.some(e => !e.busStandCode || !e.busNo)}
                >
                  {loading ? t('adding') : t('addAllEntries', { count: entries.length })}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={handleReset}
                  disabled={loading}
                >
                  {t('resetForm')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      <style jsx="true">{`
        :root {
          --primary-color: #FFFDE7;
          --secondary-color: #FFE082;
          --accent-color: #FFB300;
          --error-color: #F44336;
          --border-color: #FFE082;
          --info-color: #2196F3;
          --success-color: #4CAF50;
        }

        .timetable-container {
          padding: 20px;
          background-color: #FFFEF5;
          min-height: 100vh;
        }

        .back-button-container {
          margin-bottom: 20px;
        }

       

        .btn-back:hover {
          background: #e9ecef;
          transform: translateY(-1px);
        }

        .card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(255, 224, 130, 0.2);
          overflow: hidden;
          border: 1px solid var(--border-color);
          max-width: 900px;
          margin: 0 auto;
        }

        .card-header {
          background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
          color: #8D6E00;
          padding: 20px 30px;
          border-bottom: 2px solid var(--accent-color);
        }

        .card-header h2 {
          margin: 0;
          font-size: 26px;
          font-weight: 600;
        }

        .card-header .subtitle {
          margin: 8px 0 0 0;
          font-size: 14px;
          opacity: 0.8;
        }

        .card-body {
          padding: 30px;
          background: linear-gradient(to bottom, white, #FFFEF5);
        }

        .loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          background: rgba(255, 253, 231, 0.8);
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid var(--secondary-color);
          border-top: 4px solid var(--accent-color);
          border-radius: 50%;
          animation: spin 1.2s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .success-message {
          background: linear-gradient(135deg, #E8F5E9, #C8E6C9);
          border: 2px solid var(--success-color);
          color: #2E7D32;
          padding: 16px 20px;
          border-radius: 8px;
          margin-bottom: 24px;
          font-weight: 500;
          text-align: center;
        }

        .timetable-form {
          width: 100%;
        }

        .entry-card {
          background: #FFFEF9;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
          position: relative;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .entry-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px dashed var(--border-color);
        }

        .entry-header h3 {
          margin: 0;
          color: #8D6E00;
          font-size: 18px;
          font-weight: 600;
        }

        .btn-remove {
          background: var(--error-color);
          color: white;
          border: none;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-remove:hover {
          background: #D32F2F;
          transform: scale(1.1);
        }

        .entry-divider {
          border: none;
          border-top: 2px dashed var(--border-color);
          margin: 30px 0;
        }

        .form-row {
          display: flex;
          gap: 24px;
          margin-bottom: 20px;
        }

        @media (max-width: 768px) {
          .form-row {
            flex-direction: column;
            gap: 18px;
          }
        }

        .form-group {
          flex: 1;
        }

        .form-group label {
          display: block;
          margin-bottom: 10px;
          font-weight: 600;
          color: #8D6E00;
          font-size: 15px;
        }

        .form-group select,
        .form-group input[type="time"] {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          font-size: 15px;
          transition: all 0.3s ease;
          background: white;
        }

        .form-group select:focus,
        .form-group input[type="time"]:focus {
          outline: none;
          border-color: var(--accent-color);
          box-shadow: 0 0 0 3px rgba(255, 179, 0, 0.2);
        }

        .form-group select.error,
        .form-group input[type="time"].error {
          border-color: var(--error-color);
          background: #FFEBEE;
        }

        .form-group select:disabled {
          background: #fafafa;
          color: #666;
          cursor: not-allowed;
          border-color: #e0e0e0;
        }

        .error-text {
          color: var(--error-color);
          font-size: 13px;
          margin-top: 6px;
          display: block;
          font-weight: 500;
        }

        .info-text {
          color: var(--info-color);
          font-size: 13px;
          margin-top: 6px;
          display: block;
          font-style: italic;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          padding: 12px 0;
        }

        .checkbox-input {
          display: none;
        }

        .checkbox-custom {
          width: 24px;
          height: 24px;
          border: 2px solid var(--border-color);
          border-radius: 6px;
          margin-right: 12px;
          position: relative;
          transition: all 0.3s ease;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .checkbox-input:checked + .checkbox-custom {
          background: var(--accent-color);
          border-color: var(--accent-color);
        }

        .checkbox-input:checked + .checkbox-custom::after {
          content: '✓';
          color: white;
          font-size: 16px;
          font-weight: bold;
        }

        .checkbox-text {
          font-size: 15px;
          font-weight: 500;
          color: #8D6E00;
        }

        .btn-duplicate {
          background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
          color: #1565C0;
          border: 2px solid #90CAF9;
          padding: 10px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 12px;
        }

        .btn-duplicate:hover:not(:disabled) {
          background: linear-gradient(135deg, #BBDEFB, #90CAF9);
          transform: translateY(-2px);
        }

        .btn-duplicate:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .form-actions-container {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 2px solid var(--border-color);
        }

        .entry-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .btn-add-entry {
          background: linear-gradient(135deg, #E8F5E9, #C8E6C9);
          color: #2E7D32;
          border: 2px solid #81C784;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-add-entry:hover {
          background: linear-gradient(135deg, #C8E6C9, #A5D6A7);
          transform: translateY(-2px);
        }

        .entry-count {
          color: #8D6E00;
          font-size: 14px;
          font-weight: 500;
          padding: 8px 16px;
          background: #FFF9C4;
          border-radius: 6px;
          border: 1px solid var(--border-color);
        }

        .form-actions {
          display: flex;
          gap: 16px;
          justify-content: flex-end;
        }

        .btn-primary,
        .btn-secondary {
          padding: 14px 28px;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--accent-color), #FF8F00);
          color: white;
          box-shadow: 0 4px 12px rgba(255, 179, 0, 0.3);
          min-width: 200px;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #FF8F00, #F57C00);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(255, 179, 0, 0.4);
        }

        .btn-primary:disabled {
          background: linear-gradient(135deg, #FFE082, #FFD54F);
          color: #FFB300;
          cursor: not-allowed;
          box-shadow: none;
        }

        .btn-secondary {
          background: linear-gradient(135deg, #FFF9C4, #FFF59D);
          color: #8D6E00;
          border: 2px solid var(--border-color);
          box-shadow: 0 4px 8px rgba(255, 224, 130, 0.2);
        }

        .btn-secondary:hover {
          background: linear-gradient(135deg, #FFF59D, #FFF176);
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(255, 224, 130, 0.3);
        }

        /* Responsive adjustments */
        @media (max-width: 600px) {
          .card-body {
            padding: 20px;
          }
          
          .entry-card {
            padding: 15px;
          }
          
          .form-actions {
            flex-direction: column;
          }
          
          .btn-primary,
          .btn-secondary {
            width: 100%;
          }
          
          .entry-controls {
            flex-direction: column;
            gap: 10px;
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
};

export default AddTimetablePage;